<template>
  <Component
    :is="tag"
    class="rs-button"
    :class="btnClasses"
    :nav-link="navLink"
    :type="type"
    @click="click"
  >
    <RsIcon
      v-if="currentIcon"
      :icon="currentIcon"
      :weight="currentWeight"
    />
    <slot v-if="$slots['default'] && !iconOnly">
      <span>{{ text }}</span>
    </slot>
  </Component>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

type ButtonDetails = {
  icon?: string,
  text?: string,
  state?: string,
};

export default defineComponent({
  name: 'RsButton',

  props: {
    /**
     * I don't like these being here, but it makes Typescript happy
     */
    buttons: {
      type: Array as () => Array<ButtonDetails>,
      default: () => [],
    },

    /**
       * Use FontAwesome icon name (https://fontawesome.com/icons?d=gallery)
       */
    icon: {
      type: String,
      default: '',
    },

    /**
       * Use FontAwesome icon weight (far,fal,fas,fad)
       */
    weight: {
      type: String,
      default: 'far',
      validator: (prop:string) => [
        'far',
        'fal',
        'fas',
        'fad',
      ].includes(prop),
    },

    /**
        * Specify a custom tag used on the root element.
        */
    tag: {
      type: String,
      default: 'button',
    },
    /**
       * Color variants: primary | secondary | secondary-light | success | warning | error | info | dark | light | white | black
       *
       */
    variant: {
      type: String,
      default: 'primary',
      validator: (prop: string) => [
        'primary',
        'secondary',
        'secondary-light',
        'success',
        'warning',
        'error',
        'info',
        'secondary-alt',
        'dark',
        'light',
        'white',
        'black',
        'cancel',
        'delete',
        'border',
      ].includes(prop),
    },
    /**
        * Adds a `border-radius` to give it a pill shape.
        */
    hollow: {
      type: Boolean,
      default: false,
    },
    /**
        * Adds a `border-radius` to give it a pill shape.
        */
    rounded: {
      type: Boolean,
      default: false,
    },
    /**
        * Removes background and border colors.
        */
    text: {
      type: Boolean,
      default: false,
    },
    /**
        * Removes text styling and padding, while maintaining the standard button height.
      */
    plain: {
      type: Boolean,
      default: false,
    },
    /**
        * Adds the `nav-link` class.
        */
    navLink: {
      type: Boolean,
      default: false,
    },

    unstyled: {
      type: Boolean,
      default: false,
    },

    /**
        * Removes side padding.
        */
    iconOnly: {
      type: Boolean,
      default: false,
    },

    /**
     *  Set button activate
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Set button disability
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: 'button',
    },

    size: {
      type: String,
      default: 'md',
      validator: (prop: string) => [
        'md',
        'xxl',
      ].includes(prop),
    },
  },
  data() {
    return {
      /*
       * currentIcon and currentWeight allow these to be changed dynamically
       * rather than being fixed properties
       */
      currentIcon: this.icon,
      currentWeight: this.weight,
      myToggle: false,
    };
  },
  computed: {
    btnClasses() {
      const slots = Object.keys(this.$slots);

      return {
        'rs-button--icon-only': !slots.includes('default') || this.iconOnly,
        'rs-button--plain': this.plain,
        'rs-button--text': this.text,
        'rs-button--rounded': this.rounded,
        'rs-button--hollow': this.hollow,
        'rs-button--icon': this.icon,
        'nav-link': this.navLink,
        'rs-button--primary': this.variant === 'primary',
        'rs-button--secondary-light': this.variant === 'secondary' || this.variant === 'secondary-light',
        'rs-button--success': this.variant === 'success',
        'rs-button--warning': this.variant === 'warning',
        'rs-button--error': this.variant === 'error',
        'rs-button--info': this.variant === 'info',
        'rs-button--light': this.variant === 'light',
        'rs-button--dark': this.variant === 'dark',
        'rs-button--secondary-alt': this.variant === 'secondary-alt',
        'rs-button--white': this.variant === 'white',
        'rs-button--black': this.variant === 'black',
        'rs-button--cancel': this.variant === 'cancel',
        'rs-button--delete': this.variant === 'delete',
        'rs-button--border': this.variant === 'border',
        'rs-button--unstyled': this.unstyled,
        'rs-button--active': this.active,
        'rs-button--disabled': this.disabled,
        [`rs-button-${this.size}`]: true,
      };
    },
  },

  methods: {
    click(ev: any): void {
      if (this.disabled) {
        return;
      }
      this.$emit('click', ev);
    },
    changeIcon(icon:string, weight = ''): void {
      this.currentIcon = icon;
      if (weight) this.currentWeight = weight;
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.rs-button{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center;
  height: 40px;
  padding: 0 1rem;
  line-height: 1.2rem;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: $global-radius;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  text-transform: capitalize;
  transition: all .25s ease;

  i + div,
  i + span{
    display: inline;
    margin-left: 0.5rem;
  }

  &--rounded{
    border-radius: 1.25rem;
  }
  @each $name, $color in $colors {
    &--#{$name} {
      color: $white;
      background-color: $color;
      &:hover,
      &.active{
        background-color: darken($color, 5%);
        border-color: darken($color, 5%);
      }
      &.rs-button--text,
      &.rs-button--hollow{
        background-color: transparent;
        color: $color;
        &:hover{
          color: $white;
          background-color: $color;
        }
      }
      &.rs-button--text{
        border-color: transparent;
      }
      &.rs-button--hollow{
        border-color: $color;
      }
    }
  }
  &--primary {
    &:hover {
      background-color: #04977C;
      border-color: #04977C;
    }
  }
  &--secondary{
    background-color: $secondary-color;
    &:hover {
      background-color: #653ADC;
      border-color: #653ADC;
    }
    &.rs-button--hollow{
      border-color: $secondary-color;
      color: $secondary-color;
      &:hover{
        color: $white;
        border-color: $secondary-color;
      }
    }
  }
  &--secondary-light{
    background-color: $secondary-color;
    &.rs-button--hollow{
      border-color: $secondary-color;
      color: $secondary-color;
      &:hover{
        color: $white;
        border-color: $secondary-color-dark;
      }
    }
  }

  &--light{
    color: $dark-gray;
    background: $white;
    &:hover{
      color: $dark-gray;
      background: $extra-light-gray;
    }
    &.rs-button--hollow{
      color: $dark-gray;
      &:hover{
        color: $dark-gray;
      }
    }
  }

  &--secondary-alt{
    color: $tertiary-dark;
    background: $tertiary-light-purple;
    &.rs-button--active{
        background: $tertiary-dark;
        color: #ffffff;
        &:hover{
        background: $tertiary-dark;
        color: #ffffff;
      }
    }
    &:hover{
      color: $tertiary-dark;
      background: $tertiary-gray;
    }
  }

  &--white{
    &.rs-button--hollow{
      &:hover{
        color: $secondary-color-dark;
      }
    }
  }

  &--border{
    border: solid 1px $tertiary-gray;

    &:hover{
      background: $tertiary-light-gray;
    }
  }

  &--cancel{
    font-weight: 500;
    background-color: transparent;
    border-color: $extra-light-gray;
    color: $tertiary-muted;
    &:hover{
      background-color: $tertiary-light-purple;
      border-color: $tertiary-light-purple;
      font-weight: bold;
      color: $secondary-color-dark;
    }
  }
  &--delete{
    background-color: transparent;
    border: none;
    color: $error;
    &:hover{
      color: darken($error, 20%);
      background: transparent;
    }
  }
  &--plain{
    padding: 0;
    color: $dark-gray;
    border-color: transparent;
    background-color: transparent;
    letter-spacing: initial;
    text-transform: none;
    font-weight: 400;
    font-size: 1rem;
    &:hover{
      border-color: transparent;
      background-color: transparent;
      color: $primary-color;
    }
  }
  &--icon{
    i{
      margin-right: 0.5rem;
    }
  }
  &--icon-only{
    padding: 0;
    width: 40px;
    height: 40px;
    i{
      margin: 0;
    }
  }
  &--active {
    color: $white;
    background: $secondary-color;
  }
  &--disabled{
    border-color: transparent;
    cursor: default !important;
    background-color: $tertiary-gray;
    color: #3D4756;
    &:hover{
      background-color: $tertiary-gray;
      color: #3D4756;
      border-color: transparent;
    }
  }

  &--unstyled {
    border: none;
    background: none;
    padding: 0rem 0.5rem 0rem 0.5rem;
    height: auto;
    color: $gray-700;
    font-size: 1.1rem;

    &:hover {
      color: $gray-900;
      background: none;
      border: none;
    }
  }

  &.rs-button-xxl {
    border-width: 3px;
    font-size: 28px;
    line-height:1.2;
    height: 136px;
    color: $black;
    padding: 3rem;

    &:hover {
      border-color: $secondary-color;
    }
  }
}
</style>
